import { selOrganizationUsers } from '@features/st-organizations/st-organization-module'
import { deliveryChecklistEmptyState } from '../checklists/selectors'
import { ChecklistDialogs } from '~/features/checklists/dialogs'
import type { ChecklistMessage } from '~/features/checklists/module'
import { IntakeChecklist } from '~/features/checklists/intake/list'
import type { STFolderReadyState } from './st-folder-types'
import { DeliveryChecklist } from '~/features/checklists/delivery/list'
import { DocumentViewport, Layer, Page, selViewerPageTransform } from '@st/document-viewer-kit'
import { useDraggable } from '@st/react-util/use-draggable'
import { STChecklistItem } from '@st/sdk'
import { match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { DocViewerScaffold } from '~/st-doc-viewer'
import { useSendViewerMessage } from './st-folder-hooks'
import { selFolderReadyState, checklistViewerModel } from './st-folder-selectors'
import { navigateToDocument } from './st-folder-focus'
import { formatFolderEntityName } from './st-folder-display'
import { openMissingDocumentsDialog } from './st-send-client-organizer-module'

export function STFormationChecklistViewer() {
  const state = useSelect('stFolder', selFolderReadyState)
  if (!state) {
    return null
  }

  return <STFormationChecklistViewerInner state={state} />
}

function STFormationChecklistViewerInner({ state }: { state: STFolderReadyState }) {
  const stFolder = useProcess('stFolder')
  const stSendClientOrganizer = useProcess('stSendClientOrganizer')
  const organizationUsers = useSelect('stOrganization', selOrganizationUsers)

  const sendChecklist = (message: ChecklistMessage) => stFolder.send({ type: 'checklist', message })
  const dialog = state.checklist.dialog
  const folderState = state.folderState
  const folderId = folderState.folderId
  const isArchived = folderState.folder.archived
  const entityName = formatFolderEntityName(folderState.folder.entities, 'client')
  const year = folderState.folder.year
  const type = folderState.folder.type
  const sourceId = folderState.folder.sourceId
  const model = checklistViewerModel(state)

  function onSelectDocument(documentId: string) {
    stFolder.send({ type: 'navigationRequested', request: navigateToDocument(documentId) })
  }

  function onRequestItems() {
    stSendClientOrganizer.send(openMissingDocumentsDialog(folderId, organizationUsers))
  }

  const { dragState, handlers } = useDraggable<STChecklistItem>({
    onDrop: (e) => {
      // Intake and tax payment lists share this drag state, so ignore drops across phases.
      if (e.item.phase == e.target.phase) {
        sendChecklist({
          type: 'checklistItemMoved',
          item: e.item,
          referenceItem: e.target,
          relation: e.relation
        })
      }
    },
    getKey: (item) => item.id
  })

  const viewerState = state.viewer

  const sendViewerMessage = useSendViewerMessage(stFolder.send)
  const pageTransform = selViewerPageTransform(viewerState.viewer)

  return (
    <>
      <DocViewerScaffold>
        <DocumentViewport
          send={sendViewerMessage}
          status={viewerState.pagesStatus}
          showPages={viewerState.viewer.viewportMeasurement != null}
          pagesToRender={viewerState.viewportState.pagesToRender}
          pageTransform={pageTransform}
        >
          {viewerState.pages.map((pageStructure) => {
            const page = model.pagesByNumber[pageStructure.pageNumber]
            if (!page) {
              return null
            }

            return (
              <Page
                key={pageStructure.pageNumber}
                page={pageStructure}
                topDivider={match(pageStructure.pageNumber)
                  .with(1, () => null)
                  .otherwise(() => undefined)}
                bottomDivider={null}
              >
                <Layer>
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      padding: '60px 80px',
                      background: 'white'
                    }}
                  >
                    {match(page.phase)
                      .with('delivery', () => (
                        <DeliveryChecklist
                          emptyState={deliveryChecklistEmptyState(
                            folderState.deliveryChecklistGeneration
                          )}
                          send={sendChecklist}
                          rows={page.rows}
                          onSelectDocument={onSelectDocument}
                          isArchived={isArchived}
                          clientName={entityName}
                          dragHandlers={handlers}
                          dragState={dragState}
                        />
                      ))
                      .with('intake', () => (
                        <IntakeChecklist
                          send={sendChecklist}
                          rows={page.rows}
                          entityName={entityName}
                          year={year}
                          type={type}
                          sourceId={sourceId}
                          isArchived={isArchived}
                          page={page}
                          onSelectDocument={onSelectDocument}
                          onRequestItems={onRequestItems}
                          dragHandlers={handlers}
                          dragState={dragState}
                        />
                      ))
                      .exhaustive()}
                  </div>
                </Layer>
              </Page>
            )
          })}
        </DocumentViewport>
      </DocViewerScaffold>
      <ChecklistDialogs
        dialog={dialog}
        send={sendChecklist}
        documentTypes={model.documentTypes}
        reasons={model.reasons}
        sections={model.sections}
      />
    </>
  )
}
